/* eslint-disable react/prop-types */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Wallet, CaretRight } from "@phosphor-icons/react";

/**
 * CreditsCard — compact widget for the main Dashboard.
 *
 * Shows the available "operational credits" with a thin progress bar
 * that colours from emerald → amber → rose as usage crosses the
 * 80%/90%/100% thresholds. Tap-target sends the user to /app/financeiro.
 *
 * The label intentionally avoids any technical wording ("tokens",
 * "calls", "messages"). The product positioning is a "franquia
 * operacional" — never a per-message bill.
 */
export default function CreditsCard() {
  const [w, setW] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    api.get("/credits/wallet")
      .then((r) => alive && setW(r.data))
      .catch(() => alive && setW(null))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, []);

  if (loading) {
    return (
      <Link
        to="/app/financeiro"
        data-testid="dashboard-credits-card-loading"
        className="block bg-white border border-slate-200 rounded-md p-4 hover:shadow-sm transition-shadow animate-pulse"
      >
        <div className="h-3 w-24 bg-slate-100 rounded mb-2" />
        <div className="h-6 w-32 bg-slate-100 rounded" />
      </Link>
    );
  }

  if (!w) {
    return (
      <Link
        to="/app/financeiro"
        data-testid="dashboard-credits-card"
        className="block bg-white border border-slate-200 rounded-md p-4 hover:shadow-sm transition-shadow"
      >
        <div className="text-[10px] uppercase tracking-[0.18em] text-slate-400 font-medium">Créditos</div>
        <div className="text-slate-500 text-sm mt-1">Ver financeiro</div>
      </Link>
    );
  }

  const pct = Math.min(100, Math.round(w.percent_used || 0));
  const barColor =
    w.threshold === "critical" ? "bg-rose-500"
      : w.threshold === "warn90" ? "bg-orange-500"
        : w.threshold === "warn80" ? "bg-amber-500"
          : "bg-emerald-500";

  return (
    <Link
      to="/app/financeiro"
      data-testid="dashboard-credits-card"
      className="block bg-white border border-slate-200 rounded-md p-4 hover:shadow-sm hover:border-slate-300 transition-all group"
    >
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.18em] text-slate-400 font-medium">
          <Wallet size={11} weight="regular" />
          Créditos operacionais
        </div>
        <CaretRight size={12} className="text-slate-300 group-hover:text-slate-500 transition-colors" />
      </div>
      <div className="text-2xl font-semibold text-slate-900 tabular-nums" data-testid="dashboard-credits-balance">
        {(w.balance || 0).toLocaleString("pt-BR")}
      </div>
      <div className="mt-2 h-1.5 w-full bg-slate-100 rounded-full overflow-hidden">
        <div
          className={`h-full ${barColor} transition-all duration-700 ease-out`}
          style={{ width: `${pct}%` }}
        />
      </div>
      <div className="mt-1.5 flex items-center justify-between text-[11px]">
        <span className="text-slate-500">
          {pct}% usado este mês
        </span>
        <span className="text-slate-400">
          franquia {(w.monthly_grant || 0).toLocaleString("pt-BR")}
        </span>
      </div>
    </Link>
  );
}
